import { CollapsibleFilters } from '../common/CollapsibleFilters';
import { useCollectionPage } from '../jobs/JobWorkspace';
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Tooltip
} from '@mui/joy';
import {
  Calendar,
  Eye,
  List as ListIcon,
  MagnifyingGlass,
  MapPin,
  Square,
  SquaresFour
} from '@phosphor-icons/react';
import type { Measurement } from '../../types';
import { useMeasurements } from '../../hooks/measurements';
import { EagleViewStatusChip } from './EagleViewStatusChip';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';

interface MeasurementsListProps {
  onEditMeasurement?: (measurement: Measurement) => void;
  onViewMeasurement?: (measurement: Measurement) => void;
  selectedJobId?: number;
  selectedOpportunityId?: number;
  hideEmptyState?: boolean;
}

type ViewMode = 'rows' | 'cards';
type SortMode = 'newest' | 'oldest' | 'address' | 'area';

const VIEW_STORAGE_KEY = 'measurements-view-mode';

const getStatusColor = (status: string): 'success' | 'warning' | 'neutral' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
      return 'In Progress';
    case 'draft':
    default:
      return 'Draft';
  }
};

export const MeasurementsList: React.FC<MeasurementsListProps> = ({
  onViewMeasurement,
  selectedJobId,
  selectedOpportunityId,
  hideEmptyState = false
}) => {
  const { data: measurementsData, isLoading, error } = useMeasurements(selectedJobId, selectedOpportunityId);

  // The full toolbar (search/sort/view toggle) only makes sense on the main
  // Measurements page; embedded lists (job / opportunity detail) stay compact.
  const showToolbar = !selectedJobId && !selectedOpportunityId;

  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'rows';
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'cards' ? 'cards' : 'rows';
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortMode>('newest');

  const changeView = (next: ViewMode) => {
    setView(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    }
  };

  // The API client already extracts results from paginated responses
  const measurements: Measurement[] = useMemo(() => {
    if (Array.isArray(measurementsData)) return measurementsData;
    if (
      measurementsData &&
      typeof measurementsData === 'object' &&
      'results' in measurementsData &&
      Array.isArray((measurementsData as { results?: Measurement[] }).results)
    ) {
      return (measurementsData as { results: Measurement[] }).results;
    }
    return [];
  }, [measurementsData]);

  const filteredMeasurements = useMemo(() => {
    let list = [...measurements];
    const query = search.trim().toLowerCase();
    if (query) {
      list = list.filter((m) =>
        [m.address, m.opportunity_name, m.job_title, m.notes, m.created_by_name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((m) => m.status === statusFilter);
    }
    switch (sortBy) {
      case 'oldest':
        list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'address':
        list.sort((a, b) => (a.address || '').localeCompare(b.address || ''));
        break;
      case 'area':
        list.sort((a, b) => (b.total_area || 0) - (a.total_area || 0));
        break;
      case 'newest':
      default:
        list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }
    return list;
  }, [measurements, search, statusFilter, sortBy]);

  const collectionPage = useCollectionPage(showToolbar ? filteredMeasurements : measurements, 'measurements');
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography level="body-sm">Loading measurements...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        Error loading measurements. Please try again.
      </Alert>
    );
  }

  if (measurements.length === 0) {
    if (hideEmptyState) {
      return null;
    }
    return (
      <Alert color="neutral">
        No measurements found. Create your first measurement by searching for a property address.
      </Alert>
    );
  }

  const renderLinkedTo = (measurement: Measurement) => {
    if (measurement.opportunity_name) return `Opp: ${measurement.opportunity_name}`;
    if (measurement.job_title) return `Job: ${measurement.job_title}`;
    return '—';
  };

  const renderMeasurementCard = (measurement: Measurement) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        cursor: onViewMeasurement ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onViewMeasurement ? {
          borderColor: 'primary.400',
          boxShadow: 'md',
          transform: 'translateY(-2px)'
        } : {}
      }}
      onClick={() => onViewMeasurement?.(measurement)}
    >
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Address */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MapPin size={18} color="var(--joy-palette-primary-500)" />
            <Typography level="title-md" sx={{ fontWeight: 'bold' }}>
              {measurement.address}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {measurement.opportunity_name || measurement.job_title
                ? renderLinkedTo(measurement)
                : 'No linked opportunity or job'}
            </Typography>
            <Chip size="sm" variant="soft" color={getStatusColor(measurement.status)}>
              {getStatusLabel(measurement.status)}
            </Chip>
            <EagleViewStatusChip status={measurement.eagleview_status} size="sm" />
          </Stack>
        </Box>

        {/* Info Grid */}
        <Box sx={{ flexGrow: 1 }}>
          {measurement.total_area && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Square size={14} />
              <Typography level="body-sm">
                {measurement.total_area.toLocaleString()} sq ft
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calendar size={14} />
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {new Date(measurement.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {measurement.notes && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {measurement.notes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const rowColumns: ResponsiveColumn<Measurement>[] = [
    {
      key: 'address',
      sortValue: (row) => row.address,
      header: 'Address',
      mobileVariant: 'header',
      render: (m) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <MapPin size={16} color="var(--joy-palette-primary-500)" />
          <Box sx={{ minWidth: 0 }}>
            <Typography level="title-sm">{m.address}</Typography>
            {m.notes && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                {m.notes}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: 'linked',
      header: 'Linked To',
      width: '18%',
      render: (m) => (
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {renderLinkedTo(m)}
        </Typography>
      ),
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      width: '110px',
      render: (m) => (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size="sm" variant="soft" color={getStatusColor(m.status)}>
            {getStatusLabel(m.status)}
          </Chip>
          <EagleViewStatusChip status={m.eagleview_status} size="sm" />
        </Stack>
      ),
    },
    {
      key: 'area',
      sortValue: (row) => Number(row.total_area ?? 0),
      header: 'Area',
      width: '110px',
      render: (m) => (
        <Typography level="body-sm">
          {m.total_area ? `${m.total_area.toLocaleString()} sq ft` : '—'}
        </Typography>
      ),
    },
    {
      key: 'created',
      sortValue: (row) => row.created_at ? new Date(row.created_at) : null,
      header: 'Created',
      width: '130px',
      render: (m) => (
        <Box>
          <Typography level="body-sm">{new Date(m.created_at).toLocaleDateString()}</Typography>
          {m.created_by_name && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {m.created_by_name}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const listToRender = collectionPage.rows;

  return (
    <Box>
      {collectionPage.controls}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography level="h4">
          ApexOS Quick Measurements (
          {showToolbar && listToRender.length !== measurements.length
            ? `${listToRender.length} of ${measurements.length}`
            : measurements.length}
          )
        </Typography>

        {showToolbar && (
          <Sheet
            variant="soft"
            sx={{ borderRadius: 'md', p: 0.25, gap: 0.25, display: 'flex', alignSelf: { xs: 'flex-end', sm: 'auto' } }}
          >
            <Tooltip title="Row view" size="sm">
              <IconButton
                size="sm"
                variant={view === 'rows' ? 'solid' : 'plain'}
                color={view === 'rows' ? 'primary' : 'neutral'}
                aria-label="Row view"
                onClick={() => changeView('rows')}
              >
                <ListIcon size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Card view" size="sm">
              <IconButton
                size="sm"
                variant={view === 'cards' ? 'solid' : 'plain'}
                color={view === 'cards' ? 'primary' : 'neutral'}
                aria-label="Card view"
                onClick={() => changeView('cards')}
              >
                <SquaresFour size={16} />
              </IconButton>
            </Tooltip>
          </Sheet>
        )}
      </Stack>

      {showToolbar && (
        <CollapsibleFilters><Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <Input
            startDecorator={<MagnifyingGlass size={16} />}
            placeholder="Search address, opportunity, job, or notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Select
            value={statusFilter}
            onChange={(_, value) => value && setStatusFilter(value)}
            sx={{ minWidth: 150 }}
          >
            <Option value="all">All Statuses</Option>
            <Option value="draft">Draft</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="completed">Completed</Option>
          </Select>
          <Select
            value={sortBy}
            onChange={(_, value) => value && setSortBy(value as SortMode)}
            sx={{ minWidth: 160 }}
          >
            <Option value="newest">Newest first</Option>
            <Option value="oldest">Oldest first</Option>
            <Option value="address">Address A–Z</Option>
            <Option value="area">Largest area</Option>
          </Select>
        </Stack></CollapsibleFilters>
      )}

      {showToolbar && listToRender.length === 0 ? (
        <Alert color="neutral">
          No measurements match your search or filters.
        </Alert>
      ) : showToolbar && view === 'rows' ? (
        <ResponsiveTable<Measurement>
          rows={listToRender}
          getRowKey={(m) => m.id}
          columns={rowColumns}
          actionsHeader=""
          actionsWidth="56px"
          rowActions={(m) => (
            <Tooltip title="View measurement" size="sm">
              <IconButton
                size="sm"
                variant="plain"
                color="primary"
                aria-label="View measurement"
                onClick={() => onViewMeasurement?.(m)}
              >
                <Eye size={18} />
              </IconButton>
            </Tooltip>
          )}
        />
      ) : (
        <Grid container spacing={3}>
          {listToRender.map((measurement) => (
            <Grid key={measurement.id} xs={12} md={6} lg={4}>
              {renderMeasurementCard(measurement)}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
